import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { profileAnalyticsService } from '../../../services/profileAnalyticsService';
import { useAuth } from '../../../contexts/AuthContext';

const VisitorsList = () => {
  const { userProfile } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisitors();
  }, [userProfile]);

  const fetchVisitors = async () => {
    try {
      const playerId = userProfile?.assignedProfileId || userProfile?.assigned_profile_id;
      if (!playerId) {
        setLoading(false);
        return;
      }

      const response = await profileAnalyticsService?.getVisitors(playerId, { limit: 10 });
      setVisitors(response?.data || []);
    } catch (error) {
      console.error('Error fetching visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVisitorTypeIcon = (type) => {
    const icons = {
      club: 'Building',
      scout: 'Users',
      sports_director: 'Award',
      fifa_agent: 'Briefcase',
      club_scouter: 'Search',
      other: 'User'
    };
    return icons?.[type] || 'User';
  };

  const getVisitorTypeColor = (type) => {
    const colors = {
      club: 'text-accent bg-accent/10',
      scout: 'text-success bg-success/10',
      sports_director: 'text-secondary bg-secondary/10',
      fifa_agent: 'text-warning bg-warning/10',
      club_scouter: 'text-primary bg-primary/10',
      other: 'text-muted-foreground bg-muted'
    };
    return colors?.[type] || colors?.other;
  };

  const formatVisitorType = (type) => {
    const labels = {
      club: 'Club',
      scout: 'Scout',
      sports_director: 'Sports Director',
      fifa_agent: 'FIFA Agent',
      club_scouter: 'Club Scouter',
      other: 'Visitor'
    };
    return labels?.[type] || 'Visitor';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (visitors?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-bold text-foreground mb-4 font-headline">Recent Visitors</h3>
        <div className="text-center py-8">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No visitors yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-foreground font-headline">Recent Visitors</h3>
        <span className="text-sm text-muted-foreground">{visitors?.length} recent</span>
      </div>
      <div className="space-y-3">
        {visitors?.map((visitor) => (
          <div
            key={visitor?.id}
            className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-300"
          >
            <div className={`w-10 h-10 rounded-lg ${getVisitorTypeColor(visitor?.visitorType)} flex items-center justify-center flex-shrink-0`}>
              <Icon name={getVisitorTypeIcon(visitor?.visitorType)} size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-foreground line-clamp-1">{visitor?.visitorName}</p>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeAgo(visitor?.lastVisitAt)}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-1">{formatVisitorType(visitor?.visitorType)}</p>
              {visitor?.visitorOrganization && (
                <p className="text-xs text-muted-foreground line-clamp-1">{visitor?.visitorOrganization}</p>
              )}
              <div className="flex items-center gap-3 mt-2">
                {visitor?.visitorCountry && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="MapPin" size={12} />
                    {visitor?.visitorCountry}
                  </span>
                )}
                {visitor?.visitCount > 1 && (
                  <span className="text-xs text-primary font-medium">{visitor?.visitCount} visits</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisitorsList;